import type { FieldErrors } from "./submitSignup";
import { NpiLookup } from "./npiLookup";

/** Everything the sign-up steps collect; submitted as one request at the end. */
export type SignupForm = {
  npi: string;
  npiAttested: boolean;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  facilityName: string;
  facilityTypeId: number | null;
  address1: string;
  address2: string;
  city: string;
  stateAbbr: string;
  zipcode: string;
  facilityPhone: string;
  facilityFax: string;
  secondaryFacilityFax: string;
  medicalRecordsFax: string;
  password: string;
  // The password again, kept so a failed submit doesn't clear it. Not submitted.
  passwordConfirm: string;
};

export const EMPTY_SIGNUP_FORM: SignupForm = {
  npi: "",
  npiAttested: false,
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  facilityName: "",
  facilityTypeId: null,
  address1: "",
  address2: "",
  city: "",
  stateAbbr: "",
  zipcode: "",
  facilityPhone: "",
  facilityFax: "",
  secondaryFacilityFax: "",
  medicalRecordsFax: "",
  password: "",
  passwordConfirm: "",
};

export type SignupStep =
  | "role"
  | "landing"
  | "account_info"
  | "password"
  | "done";

/** Every step, in the order the user goes through them. */
export const SIGNUP_STEPS: SignupStep[] = [
  "role",
  "landing",
  "account_info",
  "password",
  "done",
];

// The step each field is entered on; every other field is on step 1.
const FIELD_STEPS: Partial<Record<keyof SignupForm, SignupStep>> = {
  npi: "landing",
  npiAttested: "landing",
  email: "landing",
  password: "password",
};

/** The step a field is entered on. */
export const fieldStep = (field: keyof SignupForm): SignupStep =>
  FIELD_STEPS[field] ?? "account_info";

/** Whether the server rejected a field on `step` that hasn't been edited since,
 * so the step can't be left until it is. */
export const hasServerErrorOn = (
  step: SignupStep,
  serverErrors: FieldErrors = {},
) =>
  (Object.keys(serverErrors) as (keyof SignupForm)[]).some(
    (field) => fieldStep(field) === step,
  );

/** The numbered steps shown in the step indicator. */
export const NUMBERED_STEPS: { step: SignupStep; label: string }[] = [
  { step: "account_info", label: "Account info" },
  { step: "password", label: "Set a password" },
];

/** What every step component receives from the sign-up page. */
export type SignupStepProps = {
  form: SignupForm;
  updateForm: (changes: Partial<SignupForm>) => void;
  // The NPI lookup's result, for prefilling step 1. Not submitted.
  npiLookup: NpiLookup | null;
  setNpiLookup: (lookup: NpiLookup) => void;
  onNext: () => void;
  onBack: () => void;
  // From submitting: errors on fields, each cleared when its field is edited.
  serverErrors?: FieldErrors;
  // Submit is in flight, or failed with a message for the whole form.
  submitting?: boolean;
  submitError?: string | null;
};
